import { jsPDF } from 'jspdf';
import { Order } from '../../types/order';
import { getPdfBackgroundDataUrl } from './pdfBackground';

const formatDate = (date: Date) => date.toLocaleDateString('pt-BR');
const formatDateTime = (value: string) => {
  const d = new Date(value);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const parseApiDate = (value: string): Date => {
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(value)) {
    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes, secondsWithMs] = timePart.split(':');
    const [seconds, milliseconds = '0'] = secondsWithMs.split('.');
    return new Date(
      year,
      month - 1,
      day,
      Number(hours),
      Number(minutes),
      Number(seconds),
      Number(milliseconds.padEnd(3, '0').slice(0, 3)),
    );
  }
  return new Date(value);
};

export const buildReportPdf = async (
  orders: Order[],
  startDateValue: string,
  endDateValue: string,
) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentTopOffset = 55;
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  const backgroundDataUrl = await getPdfBackgroundDataUrl();

  const drawBackground = () => {
    if (!backgroundDataUrl) return;
    doc.addImage(backgroundDataUrl, 'PNG', 0, 0, pageWidth, pageHeight, undefined, 'FAST');
  };

  drawBackground();

  // Parse period boundaries (local time, inclusive)
  const [sy, sm, sd] = startDateValue.split('-').map(Number);
  const periodStart = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
  const [ey, em, ed] = endDateValue.split('-').map(Number);
  const periodEnd = new Date(ey, em - 1, ed, 23, 59, 59, 999);

  const periodDays = Math.max(
    1,
    Math.ceil((periodEnd.getTime() - periodStart.getTime() + 1) / (1000 * 60 * 60 * 24)),
  );

  // Filter orders within period (all statuses)
  const ordersInPeriod = orders.filter((o) => {
    const updated = parseApiDate(o.updatedAt);
    return updated >= periodStart && updated <= periodEnd;
  });

  // Filter executed orders (CONCLUIDA) completed within the period
  const executedOrders = ordersInPeriod.filter((o) => o.status === 'CONCLUIDA');

  const publicCount = ordersInPeriod.filter((o) => o.origin === 'PORTAL_PUBLICO').length;
  const celpeCount = ordersInPeriod.filter((o) => o.cadastroCelpe).length;
  const medidaCount = ordersInPeriod.filter((o) => o.medida).length;
  const estimadaCount = ordersInPeriod.filter((o) => o.estimada).length;

  const avgExecution = executedOrders.length / periodDays;

  // Aggregate materials used across all executed orders
  const materialsMap: Record<string, number> = {};
  executedOrders.forEach((o) => {
    const cl = o.completionChecklist;
    if (!cl || !cl.materiaisUtilizados) return;
    cl.materiaisUtilizados.forEach((mat) => {
      const qty = cl.materiaisQtd?.[mat] ?? 1;
      materialsMap[mat] = (materialsMap[mat] ?? 0) + qty;
    });
  });

  // Aggregate lamps/refletores across all executed orders
  const lampEncontradaMap: Record<string, number> = {};
  const lampInstaladaMap: Record<string, number> = {};
  const refletorMap: Record<string, number> = {};
  executedOrders.forEach((o) => {
    const cl = o.completionChecklist;
    if (!cl) return;
    if (cl.lampadaEncontrada && cl.lampadaEncontrada !== 'NENHUMA') {
      lampEncontradaMap[cl.lampadaEncontrada] = (lampEncontradaMap[cl.lampadaEncontrada] ?? 0) + 1;
    }
    if (cl.lampadaInstalada && cl.lampadaInstalada !== 'NENHUMA') {
      lampInstaladaMap[cl.lampadaInstalada] = (lampInstaladaMap[cl.lampadaInstalada] ?? 0) + 1;
    }
    if (cl.refletorInstalado && cl.refletorInstalado !== 'NENHUMA') {
      refletorMap[cl.refletorInstalado] = (refletorMap[cl.refletorInstalado] ?? 0) + 1;
    }
  });

  let y = 10 + contentTopOffset;

  // ── Header ──────────────────────────────────────────────────────────────
  doc.setFillColor(52, 152, 219);
  doc.roundedRect(margin, y, contentWidth, 16, 3, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('Relatório de Execução de Ordens de Serviço', margin + 5, y + 7);
  doc.setFontSize(8.5);
  doc.text('Iluminação Pública', margin + 5, y + 12.5);

  // Period on the right side of header
  doc.setFontSize(8);
  const periodText = `Período: ${formatDate(periodStart)} a ${formatDate(periodEnd)}`;
  const periodTextWidth = doc.getTextWidth(periodText);
  doc.text(periodText, pageWidth - margin - periodTextWidth - 2, y + 12.5);

  y += 22;

  // ── Summary cards ────────────────────────────────────────────────────────
  const cardH = 16;
  const cardGap = 4;
  const cardW = (contentWidth - cardGap * 2) / 3;

  const drawCard = (x: number, label: string, value: string) => {
    doc.setDrawColor(222, 226, 230);
    doc.setFillColor(248, 249, 250);
    doc.roundedRect(x, y, cardW, cardH, 2, 2, 'FD');
    doc.setTextColor(73, 80, 87);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text(label, x + 3, y + 5);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(value, x + 3, y + 12);
  };

  // Card 1: Period
  drawCard(margin, 'PERÍODO (DIAS)', String(periodDays));

  // Card 2: Executed orders
  const x2 = margin + cardW + cardGap;
  drawCard(x2, 'ORDENS EXECUTADAS', String(executedOrders.length));

  // Card 3: Average rate
  const x3 = margin + (cardW + cardGap) * 2;
  drawCard(x3, 'TAXA MÉDIA (OS/DIA)', avgExecution.toFixed(2));

  y += cardH + 4;

  // Row 2: Public, Cadastro Celpe, Medida
  drawCard(margin, 'PÚBLICO', String(publicCount));
  drawCard(margin + cardW + cardGap, 'CADASTRO CELPE', String(celpeCount));
  drawCard(margin + (cardW + cardGap) * 2, 'MEDIDA', String(medidaCount));

  y += cardH + 4;

  // Row 3: Estimada
  drawCard(margin, 'ESTIMADA', String(estimadaCount));

  y += cardH + 8;

  // ── Lamps / Refletores summary ──────────────────────────────────────────────
  const drawMiniTable = (title: string, map: Record<string, number>) => {
    const entries = Object.entries(map).sort((a, b) => b[1] - a[1]);
    if (entries.length === 0) return;
    if (y + 15 > pageHeight - 15) { doc.addPage(); drawBackground(); y = 15 + contentTopOffset; }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(33, 37, 41);
    doc.text(title, margin, y);
    y += 4;
    entries.forEach(([nome, qtd]) => {
      if (y + 5 > pageHeight - 15) { doc.addPage(); drawBackground(); y = 15 + contentTopOffset; }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(70, 70, 70);
      doc.text(`${nome}: ${qtd}`, margin + 4, y + 3.5);
      y += 5;
    });
    y += 4;
  };

  drawMiniTable('Lâmpadas Encontradas', lampEncontradaMap);
  drawMiniTable('Lâmpadas Instaladas', lampInstaladaMap);
  drawMiniTable('Refletores Instalados', refletorMap);

  // ── Materials section ────────────────────────────────────────────────────
  const materialEntries = Object.entries(materialsMap);

  if (materialEntries.length > 0) {
    // Page break if not enough room for title + header + at least 2 rows
    if (y + 5 + 7 + 13 > pageHeight - 15) {
      doc.addPage();
      drawBackground();
      y = 15 + contentTopOffset;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(33, 37, 41);
    doc.text('Materiais Utilizados no Período', margin, y);
    y += 5;

    // Table header
    const col1W = contentWidth * 0.75;
    const col2W = contentWidth * 0.25;
    doc.setFillColor(52, 152, 219);
    doc.rect(margin, y, contentWidth, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('Material', margin + 3, y + 5);
    doc.text('Qtd. Total', margin + col1W + 3, y + 5);
    y += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);

    materialEntries.sort((a, b) => b[1] - a[1]);

    materialEntries.forEach((entry) => {
      const [mat, qty] = entry;
      if (y + 6.5 > pageHeight - 15) {
        doc.addPage();
        drawBackground();
        y = 15 + contentTopOffset;
      }
      // Simplified row background (always light)
      doc.setFillColor(248, 249, 250);
      doc.rect(margin, y, contentWidth, 6.5, 'F');
      doc.setTextColor(33, 37, 41);
      doc.setDrawColor(222, 226, 230);
      doc.rect(margin, y, contentWidth, 6.5, 'S');
      doc.text(String(mat), margin + 3, y + 4.5);
      doc.text(String(qty), margin + col1W + 3, y + 4.5);
      y += 6.5;
    });

    y += 8;
  }

  // ── Orders table ─────────────────────────────────────────────────────────
  if (y + 5 > pageHeight - 15) {
    doc.addPage();
    drawBackground();
    y = 15 + contentTopOffset;
  }

  if (executedOrders.length === 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text('Nenhuma ordem concluída no período selecionado.', margin, y + 5);
  } else {
    // Column widths: protocolo, endereço, concluída em
    // A coluna de protocolo e maior para evitar truncamento com reticencias.
    const C = [42, contentWidth - 42 - 40, 40];
    const headers = ['Protocolo', 'Endereço', 'Concluída em'];

    // Table header row
    doc.setFillColor(52, 152, 219);
    doc.rect(margin, y, contentWidth, 9, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    let cx = margin;
    headers.forEach((h, i) => {
      doc.text(h, cx + 3, y + 6.2);
      cx += C[i];
    });
    y += 9;

    executedOrders.forEach((order) => {
      // Build materials line for this order
      const cl = order.completionChecklist;
      const matParts: string[] = [];
      let totalQty = 0;
      if (cl?.materiaisUtilizados?.length) {
        cl.materiaisUtilizados.forEach((mat) => {
          const qty = cl.materiaisQtd?.[mat] ?? 1;
          matParts.push(`${mat} (${qty})`);
          totalQty += qty;
        });
      }
      const matLine = matParts.length > 0 ? `${totalQty} itens - ${matParts.join(', ')}` : null;

      // Build info line (barramento, bairro, coordenadas)
      const coordStr = order.latitude != null && order.longitude != null
        ? `${order.latitude.toFixed(6)}, ${order.longitude.toFixed(6)}`
        : '';
      const mapsUrl = coordStr
        ? `https://maps.google.com/?q=${order.latitude},${order.longitude}`
        : '';
      const barBairro = `${order.poleBusbar ? `Barramento: ${order.poleBusbar}` : ''}${order.poleBusbar && order.neighborhood ? ' | ' : ''}${order.neighborhood ? `Bairro: ${order.neighborhood}` : ''}`.trim();
      const infoParts = [barBairro].filter((p) => p.length > 0);
      const infoLine = infoParts.length > 0 ? infoParts.join(' | ') : null;

      // Build tipo line (Público, Cadastro Celpe, Medida, Estimada)
      const tipoParts: string[] = [];
      if (order.origin === 'PORTAL_PUBLICO') tipoParts.push('Público');
      if (order.cadastroCelpe) tipoParts.push('Cadastro Celpe');
      if (order.medida) tipoParts.push('Medida');
      if (order.estimada) tipoParts.push('Estimada');
      const tipoLine = tipoParts.length > 0 ? `Tipo: ${tipoParts.join(', ')}` : null;

      // Build lampada line (Encontrada / Instalada / Refletor)
      const lampParts: string[] = [];
      if (cl?.lampadaEncontrada && cl.lampadaEncontrada !== 'NENHUMA') {
        lampParts.push(`Encontrada: ${cl.lampadaEncontrada}`);
      }
      if (cl?.lampadaInstalada && cl.lampadaInstalada !== 'NENHUMA') {
        lampParts.push(`Instalada: ${cl.lampadaInstalada}`);
      }
      if (cl?.refletorInstalado && cl.refletorInstalado !== 'NENHUMA') {
        lampParts.push(`Refletor: ${cl.refletorInstalado}`);
      }
      const lampLine = lampParts.length > 0 ? lampParts.join('  |  ') : null;

      // Heights: main row 8mm + optional sub-rows
      const mainRowH = 8;
      const infoRowH = infoLine ? 5.5 : 0;
      const tipoRowH = tipoLine ? 5.5 : 0;
      const lampRowH = lampLine ? 5.5 : 0;
      const coordRowH = coordStr ? 5.5 : 0;
      const matRowH = matLine ? 5.5 : 0;
      const totalRowH = mainRowH + infoRowH + tipoRowH + lampRowH + coordRowH + matRowH;

      // New page if needed
      if (y + totalRowH > pageHeight - 15) {
        doc.addPage();
        drawBackground();
        y = 15 + contentTopOffset;
      }

      // Main row background
      doc.setFillColor(248, 249, 250);
      doc.setDrawColor(222, 226, 230);
      doc.rect(margin, y, contentWidth, totalRowH, 'FD');

      // Main row text
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(33, 37, 41);

      const cols = [
        order.protocolNumber ?? '-',
        order.address,
        formatDateTime(order.updatedAt),
      ];

      cx = margin;
      cols.forEach((val, i) => {
        if (i === 0) {
          doc.text(String(val), cx + 3, y + 5.8);
          cx += C[i];
          return;
        }

        const maxW = C[i] - 4;
        const lines = doc.splitTextToSize(String(val), maxW);
        doc.text(lines[0] + (lines.length > 1 ? '...' : ''), cx + 3, y + 5.8);
        cx += C[i];
      });

      // Info sub-row (barramento, bairro)
      if (infoLine) {
        const infoMaxW = contentWidth - 40;
        const infoLines = doc.splitTextToSize(infoLine, infoMaxW);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.2);
        doc.setTextColor(70, 70, 70);
        doc.text(infoLines[0] + (infoLines.length > 1 ? '...' : ''), margin + 3, y + mainRowH + 3.8);
      }

      // Tipo sub-row (Público, Celpe, Medida, Estimada)
      if (tipoLine) {
        const tipoMaxW = contentWidth - 6;
        const tipoLines = doc.splitTextToSize(tipoLine, tipoMaxW);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.2);
        doc.setTextColor(103, 65, 217);
        doc.text(tipoLines[0] + (tipoLines.length > 1 ? '...' : ''), margin + 3, y + mainRowH + infoRowH + 3.8);
      }

      // Lamp sub-row (Encontrada / Instalada / Refletor)
      if (lampLine) {
        const lampMaxW = contentWidth - 6;
        const lampLines = doc.splitTextToSize(lampLine, lampMaxW);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.2);
        doc.setTextColor(25, 135, 84);
        doc.text(lampLines[0] + (lampLines.length > 1 ? '...' : ''), margin + 3, y + mainRowH + infoRowH + tipoRowH + 3.8);
      }

      // Coord sub-row (link para Google Maps)
      if (coordStr) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.2);
        doc.setTextColor(52, 152, 219);
        const coordText = `📌 ${coordStr}`;
        doc.textWithLink(coordText, margin + 3, y + mainRowH + infoRowH + tipoRowH + lampRowH + 3.8, { url: mapsUrl });
      }

      // Materials sub-row
      if (matLine) {
        const matMaxW = contentWidth - 6;
        const [matCount, ...matRest] = matLine.split(' - ');
        const matDesc = matRest.join(' - ');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.2);
        doc.setTextColor(40, 100, 160);
        doc.text(matCount + (matDesc ? ' - ' : ''), margin + 3, y + mainRowH + infoRowH + tipoRowH + lampRowH + coordRowH + 3.8);
        const countW = doc.getTextWidth(matCount + (matDesc ? ' - ' : ''));
        if (matDesc) {
          const descMaxW = contentWidth - 6 - countW;
          const descLines = doc.splitTextToSize(matDesc, descMaxW);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7.2);
          doc.setTextColor(70, 70, 70);
          doc.text(descLines[0] + (descLines.length > 1 ? '...' : ''), margin + 3 + countW, y + mainRowH + infoRowH + tipoRowH + lampRowH + coordRowH + 3.8);
        }
      }

      y += totalRowH;
    });
  }

  // ── Footer ────────────────────────────────────────────────────────────────
  // Usa o método público getNumberOfPages do jsPDF (disponível desde a versão 2.x)
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFontSize(7.5);
    doc.setTextColor(150, 150, 150);
    doc.setFont('helvetica', 'normal');
    const footerText = `Gerado em ${formatDate(new Date())}  |  Página ${p} de ${totalPages}`;
    const ftw = doc.getTextWidth(footerText);
    doc.text(footerText, (pageWidth - ftw) / 2, pageHeight - 6);
  }

  doc.save(`relatorio_os_${startDateValue}_${endDateValue}.pdf`);
};
